import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowLeft, Shield, Tag, Bitcoin, Smartphone, Copy, QrCode, Upload, X, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { fbTrack } from '../utils/pixel';

const Checkout = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [couponCode, setCouponCode] = useState(searchParams.get('coupon') || '');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [rulesVersion, setRulesVersion] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('upi'); // 'upi' (AYRONN PAY) | 'crypto' (OxaPay)

  // AYRONN PAY state
  const [upiConfig, setUpiConfig] = useState(null);
  const [showUpiForm, setShowUpiForm] = useState(false);
  const [upiForm, setUpiForm] = useState({ full_name: '', phone: '', utr: '' });
  const [upiScreenshot, setUpiScreenshot] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchPlan(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);
  useEffect(() => {
    if (couponCode) handleApplyCoupon(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    api.get('/trading-rules').then(r => setRulesVersion(r.data?.version || 1)).catch(() => setRulesVersion(1));
    api.get('/payments/ayronn-pay/config').then(r => setUpiConfig(r.data)).catch(() => {});
  }, []);

  const fetchPlan = async () => {
    try {
      const { data } = await api.get('/plans');
      setPlan(data.find(p => p.id === planId));
    } catch (error) {
      toast.error('Failed to load plan details');
      navigate('/pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post('/coupons/validate', { code: couponCode });
      setAppliedCoupon(data);
    } catch (error) {
      setAppliedCoupon(null);
    }
  };

  const finalPrice = plan && appliedCoupon ? Math.ceil(plan.price * (100 - appliedCoupon.discount_pct) / 100) : plan?.price;
  const finalPriceInr = upiConfig && finalPrice ? Math.round(finalPrice * upiConfig.usd_to_inr_rate) : null;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied!`)).catch(() => toast.error('Copy failed'));
  };

  const handleCryptoPurchase = async () => {
    if (!agreedToRules) { toast.error('Please agree to the trading rules before proceeding'); return; }
    setPurchasing(true);
    fbTrack('InitiateCheckout', {
      content_ids: [planId], content_name: plan?.name || planId, content_type: 'product',
      value: finalPrice, currency: 'USD', payment_method: 'crypto',
    });
    try {
      const { data } = await api.post('/payments/oxapay/create', {
        plan_id: planId,
        coupon_code: appliedCoupon?.code || null,
        rules_version: rulesVersion,
        rules_accepted: agreedToRules,
      });
      if (data.payment_url) {
        try {
          sessionStorage.setItem('ayronn_pending_purchase', JSON.stringify({
            order_id: data.order_id, plan_id: planId, value: finalPrice, name: plan?.name || planId, gateway: 'oxapay',
          }));
        } catch (_e) {}
        toast.success('Redirecting to crypto payment gateway...');
        window.location.href = data.payment_url;
      } else {
        await api.post(`/payments/oxapay/confirm/${data.order_id}`);
        fbTrack('Purchase', { content_ids: [planId], content_name: plan?.name || planId, content_type: 'product', value: finalPrice, currency: 'USD' });
        toast.success('Purchase successful! Your trading account is ready.');
        navigate(`/success?order=${data.order_id}`);
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Payment failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleUpiOpen = () => {
    if (!agreedToRules) { toast.error('Please agree to the trading rules first'); return; }
    fbTrack('InitiateCheckout', {
      content_ids: [planId], content_name: plan?.name || planId, content_type: 'product',
      value: finalPrice, currency: 'USD', payment_method: 'upi',
    });
    setShowUpiForm(true);
  };

  const handleUpiSubmit = async () => {
    if (!upiForm.full_name.trim()) { toast.error('Full name required'); return; }
    if (!upiForm.phone.trim()) { toast.error('Phone number required'); return; }
    if (!upiForm.utr.trim()) { toast.error('UTR / Transaction ID required'); return; }
    if (!upiScreenshot) { toast.error('Please upload your payment screenshot'); return; }
    if (upiScreenshot.size > 5 * 1024 * 1024) { toast.error('Screenshot too large (max 5MB)'); return; }

    setPurchasing(true);
    try {
      const fd = new FormData();
      fd.append('plan_id', planId);
      fd.append('full_name', upiForm.full_name.trim());
      fd.append('phone', upiForm.phone.trim());
      fd.append('utr', upiForm.utr.trim());
      fd.append('rules_version', String(rulesVersion || 1));
      fd.append('rules_accepted', 'true');
      if (appliedCoupon?.code) fd.append('coupon_code', appliedCoupon.code);
      fd.append('screenshot', upiScreenshot);

      const { data } = await api.post('/payments/ayronn-pay/submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Payment submitted! We will verify and activate your account within a few hours.');
      try {
        sessionStorage.setItem('ayronn_pending_purchase', JSON.stringify({
          order_id: data.order_id, plan_id: planId, value: finalPrice, name: plan?.name || planId, gateway: 'ayronn_pay',
        }));
      } catch (_e) {}
      navigate(`/success?order=${data.order_id}&pending=1`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Submission failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505]"><Navbar /><div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" /></div></div>;
  }
  if (!plan) {
    return <div className="min-h-screen bg-[#050505]"><Navbar /><div className="flex items-center justify-center min-h-screen"><div className="text-white">Plan not found</div></div></div>;
  }

  const getRules = () => {
    if (plan.challenge_type === '1-step') return [
      `Profit Target: ${plan.profit_target}%`,
      `Max Drawdown: ${plan.max_drawdown}%`,
      `Daily Loss Limit: ${plan.daily_loss_limit}%`,
      `Min Trading Days: ${plan.min_trading_days}`,
      'No Time Limit',
    ];
    if (plan.challenge_type === '2-step') return [
      'Phase 1 Target: 6% | Phase 2 Target: 6%',
      `Max Drawdown: ${plan.max_drawdown}%`,
      `Daily Loss Limit: ${plan.daily_loss_limit}%`,
      `Min ${plan.min_trading_days} Trading Days per Phase`,
      'No Time Limit',
    ];
    return [
      `Profit Split: ${plan.profit_split}%`,
      `Max Drawdown: ${plan.max_drawdown}%`,
      `Daily Loss Limit: ${plan.daily_loss_limit}%`,
      'Consistency Rule: No single trade > 30% of total profit',
    ];
  };

  return (
    <div className="min-h-screen bg-[#050505] bg-noise">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <button onClick={() => navigate('/pricing')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors" data-testid="back-to-pricing">
            <ArrowLeft className="w-5 h-5" /> Back to Pricing
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Order Summary */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass-heavy rounded-2xl p-8">
              <h2 className="text-2xl font-bold font-heading text-white mb-6">Order Summary</h2>

              <div className="space-y-3.5 mb-6 text-sm">
                {[
                  ['Challenge Type', <span className="capitalize">{plan.challenge_type}</span>],
                  ['Account Size', `$${plan.account_size.toLocaleString()}`],
                  ['Profit Split', `${plan.profit_split}%`],
                  ...(plan.profit_target > 0 ? [['Profit Target', `${plan.profit_target}%`]] : []),
                  ['Max Drawdown', `${plan.max_drawdown}%`],
                  ['Daily Loss Limit', `${plan.daily_loss_limit}%`],
                ].map(([label, value], i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-zinc-400">{label}</span>
                    <span className="text-white font-semibold">{value}</span>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="mb-6">
                <div className="flex items-center gap-2">
                  <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Coupon code"
                    className="flex-1 px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary outline-none" data-testid="checkout-coupon-input" />
                  <button onClick={handleApplyCoupon} className="px-4 py-2 bg-white/5 border border-white/10 text-white text-sm rounded-lg hover:bg-white/10 transition-all" data-testid="checkout-apply-coupon">Apply</button>
                </div>
                {appliedCoupon && <div className="flex items-center gap-2 mt-2 text-brand-positive text-sm"><Tag className="w-4 h-4" /> {appliedCoupon.description} applied!</div>}
              </div>

              <div className="border-t border-white/10 pt-4 mb-6">
                {appliedCoupon && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Original Price</span>
                    <span className="text-zinc-500 line-through">${plan.price}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-brand-primary font-bold text-2xl font-mono">${finalPrice}</span>
                </div>
                {paymentMethod === 'upi' && finalPriceInr && (
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-zinc-500">Approx. INR (@ ₹{upiConfig?.usd_to_inr_rate}/USD)</span>
                    <span className="text-zinc-300 font-mono">₹{finalPriceInr.toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2.5 text-sm">
                {['Instant account activation', 'Professional risk management', '24/7 customer support'].map((t, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-positive" /><span className="text-zinc-400">{t}</span></div>
                ))}
              </div>
            </motion.div>

            {/* Payment & Rules */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              {/* Trading Rules */}
              <div className="glass-heavy rounded-2xl p-6 border-l-4 border-l-brand-primary">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-brand-primary" />
                  <h3 className="text-lg font-bold text-white">Trading Rules</h3>
                </div>
                <div className="space-y-2.5">
                  {getRules().map((rule, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle2 className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      {rule}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div className="glass-heavy rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Payment Method</h3>

                <div className="mb-5">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Choose Payment Method</div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('upi')}
                      className={`relative p-4 rounded-xl border text-left transition-all ${paymentMethod === 'upi' ? 'border-brand-primary bg-brand-primary/10 shadow-[0_0_20px_rgba(0,212,255,0.25)]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                      data-testid="payment-method-upi"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Smartphone className={`w-5 h-5 ${paymentMethod === 'upi' ? 'text-brand-primary' : 'text-zinc-400'}`} />
                        <span className="text-sm font-bold text-white">AYRONN PAY</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">UPI</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">GPay</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">PhonePe</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">Paytm</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-2">Best for India · Manual verification (~2 hrs)</div>
                      {paymentMethod === 'upi' && <span className="absolute top-2 right-2 w-2 h-2 bg-brand-primary rounded-full ring-2 ring-brand-primary/40" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('crypto')}
                      className={`relative p-4 rounded-xl border text-left transition-all ${paymentMethod === 'crypto' ? 'border-[#F7931A] bg-[#F7931A]/10 shadow-[0_0_20px_rgba(247,147,26,0.2)]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                      data-testid="payment-method-crypto"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin className={`w-5 h-5 ${paymentMethod === 'crypto' ? 'text-[#F7931A]' : 'text-zinc-400'}`} />
                        <span className="text-sm font-bold text-white">Crypto</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">USDT</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">BTC</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-white/10 text-white rounded">ETH</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-2">Global · Instant activation</div>
                      {paymentMethod === 'crypto' && <span className="absolute top-2 right-2 w-2 h-2 bg-[#F7931A] rounded-full ring-2 ring-[#F7931A]/40" />}
                    </button>
                  </div>
                </div>

                <div className={`p-3 rounded-xl mb-6 ${paymentMethod === 'upi' ? 'bg-brand-primary/5 border border-brand-primary/20' : 'bg-[#F7931A]/5 border border-[#F7931A]/20'}`}>
                  <p className={`text-xs ${paymentMethod === 'upi' ? 'text-brand-primary' : 'text-[#F7931A]'}`}>
                    {paymentMethod === 'upi'
                      ? 'Pay via any UPI app (GPay / PhonePe / Paytm / BHIM). After payment, submit UTR + screenshot — verified within a few hours.'
                      : 'Secure crypto payment by OxaPay. Pay with USDT (TRC20), BTC, ETH or other cryptocurrencies. Account activates instantly.'}
                  </p>
                </div>

                {/* Agreement Checkbox */}
                <label className="flex items-start gap-3 mb-2 cursor-pointer group" data-testid="agree-rules-label">
                  <input type="checkbox" checked={agreedToRules} onChange={(e) => setAgreedToRules(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-[#050505] text-brand-primary focus:ring-brand-primary/50" data-testid="agree-rules-checkbox" />
                  <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                    I have read and agree to the <a href="/rules" target="_blank" rel="noreferrer" className="text-brand-primary underline hover:text-[#A78BFA]">Trading Rules & Conditions</a>, <a href="/risk-disclosure" target="_blank" rel="noreferrer" className="text-brand-primary underline hover:text-[#A78BFA]">Risk Disclosure</a>, and <a href="/terms" target="_blank" rel="noreferrer" className="text-brand-primary underline hover:text-[#A78BFA]">Terms</a>.
                  </span>
                </label>
                {rulesVersion && <p className="text-[10px] text-zinc-600 mb-6 ml-7">You're agreeing to Rules version {rulesVersion} · your acceptance will be timestamped and stored.</p>}

                <button
                  onClick={paymentMethod === 'upi' ? handleUpiOpen : handleCryptoPurchase}
                  disabled={purchasing || !agreedToRules}
                  className="w-full py-4 bg-brand-primary text-black font-bold rounded-xl hover:glow-primary transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="complete-purchase-btn">
                  {purchasing ? 'Processing...' : paymentMethod === 'upi' ? `Pay ₹${finalPriceInr?.toLocaleString('en-IN') || '—'} via UPI` : `Pay $${finalPrice} via Crypto`}
                </button>
                <p className="text-[10px] text-zinc-600 text-center mt-3">Payment is for participation in a trading evaluation program.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AYRONN PAY UPI Modal — Premium conversion-focused redesign */}
      {showUpiForm && upiConfig && (
        <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center p-0 md:p-4 bg-black/85 backdrop-blur-md overflow-y-auto" data-testid="upi-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full md:max-w-2xl my-0 md:my-8 max-h-screen md:max-h-[95vh] overflow-y-auto bg-gradient-to-b from-[#0A0F1C] via-[#070B14] to-[#050505] border border-brand-primary/20 rounded-none md:rounded-2xl shadow-[0_30px_90px_rgba(0,212,255,0.18)]"
          >
            {/* Sticky header with close + trust */}
            <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0A0F1C] to-[#0A0F1C]/95 backdrop-blur-md border-b border-white/8 px-5 md:px-8 pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-brand-primary/15 border border-brand-primary/30 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-4 h-4 text-brand-primary" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg md:text-xl font-black text-white truncate">AYRONN PAY</h2>
                      <span className="px-1.5 py-0.5 text-[9px] font-black bg-brand-primary/20 text-brand-primary rounded uppercase tracking-wider whitespace-nowrap">UPI · India</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 -mt-0.5">Pay → Submit → Activate</div>
                  </div>
                </div>
                <button
                  onClick={() => !purchasing && setShowUpiForm(false)}
                  className="p-2 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-white transition-all flex-shrink-0"
                  data-testid="upi-modal-close"
                ><X className="w-5 h-5" /></button>
              </div>
              {/* Trust strip */}
              <div className="flex items-center justify-between gap-2 overflow-x-auto no-scrollbar">
                {[
                  { icon: Shield, label: 'Secure' },
                  { icon: CheckCircle2, label: 'Verified ≤ 2 hrs' },
                  { icon: Tag, label: '100% Manual Review' },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 whitespace-nowrap">
                    <t.icon className="w-3 h-3 text-brand-positive" strokeWidth={2} />
                    <span className="uppercase tracking-wider">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 md:p-8 space-y-5">
              {/* Step 1 — Pay */}
              <div>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-7 h-7 rounded-full bg-brand-primary text-black font-black text-sm flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <div className="text-white font-black text-base">Pay via UPI</div>
                    <div className="text-[11px] text-zinc-500">Scan QR or copy UPI ID — pay exact amount</div>
                  </div>
                </div>

                {/* QR + Details — stacks on mobile, 2-col on tablet+ */}
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                  {/* QR Code — premium frame */}
                  <div className="sm:col-span-2 relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/30 via-transparent to-brand-gold/20 rounded-2xl blur-xl opacity-60" />
                    <div className="relative bg-white rounded-2xl p-3 sm:p-4 flex flex-col items-center shadow-[0_0_30px_rgba(0,212,255,0.15)]">
                      <img src="/upi-qr.png" alt="UPI QR Code" className="w-full h-auto max-w-[240px] aspect-square object-contain mx-auto" data-testid="upi-qr-image" />
                      <div className="mt-2 flex items-center gap-1.5 text-zinc-700 text-[10px] font-black uppercase tracking-wider">
                        <QrCode className="w-3 h-3" /> Scan with any UPI app
                      </div>
                      <div className="mt-1.5 flex items-center gap-1 text-[9px] text-zinc-500">
                        <span>GPay</span><span>·</span><span>PhonePe</span><span>·</span><span>Paytm</span><span>·</span><span>BHIM</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="sm:col-span-3 space-y-2.5">
                    {/* Amount FIRST — biggest, most important */}
                    <div className="relative">
                      <div className="absolute -inset-px bg-gradient-to-br from-brand-primary to-brand-primary/40 rounded-xl blur-sm opacity-50" />
                      <button
                        onClick={() => copyToClipboard(String(finalPriceInr), 'Amount')}
                        className="relative w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gradient-to-br from-brand-primary/15 to-brand-primary/5 border border-brand-primary/40 rounded-xl hover:border-brand-primary transition-all group"
                        data-testid="upi-copy-amount"
                      >
                        <div className="min-w-0 text-left">
                          <div className="text-[9px] text-brand-primary/80 uppercase font-black tracking-wider">Exact Amount</div>
                          <div className="font-mono text-brand-primary text-xl md:text-2xl font-black leading-tight">₹{finalPriceInr?.toLocaleString('en-IN')}</div>
                          <div className="text-[9px] text-zinc-500">≈ ${finalPrice} USD</div>
                        </div>
                        <Copy className="w-4 h-4 text-brand-primary/70 group-hover:text-brand-primary flex-shrink-0" />
                      </button>
                    </div>
                    {/* UPI ID */}
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase font-black tracking-wider mb-1">UPI ID</div>
                      <button
                        onClick={() => copyToClipboard(upiConfig.upi_id, 'UPI ID')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-[#050505] border border-white/10 rounded-lg hover:border-brand-primary/40 transition-all group"
                        data-testid="upi-copy-id"
                      >
                        <span className="font-mono text-white text-sm font-black truncate">{upiConfig.upi_id}</span>
                        <Copy className="w-3.5 h-3.5 text-zinc-500 group-hover:text-brand-primary flex-shrink-0" />
                      </button>
                    </div>
                    {/* Beneficiary Name */}
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase font-black tracking-wider mb-1">Beneficiary</div>
                      <div className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-bold capitalize">{upiConfig.display_name}</div>
                    </div>
                  </div>
                </div>

                {/* Important note */}
                <div className="mt-3 p-3 rounded-lg bg-yellow-400/[0.04] border border-yellow-400/15 flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-[11px] text-zinc-300 leading-relaxed">
                    <strong className="text-yellow-400">Important:</strong> Pay the <strong className="text-white">exact</strong> amount and save the <strong className="text-white">UTR / Reference No.</strong> from your UPI app. You'll need it in Step 2.
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/8" />
                <div className="text-[9px] text-zinc-600 uppercase tracking-[0.2em] font-black">Once Paid</div>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              {/* Step 2 — Submit Proof */}
              <div>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-7 h-7 rounded-full bg-brand-primary text-black font-black text-sm flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <div className="text-white font-black text-base">Submit Payment Proof</div>
                    <div className="text-[11px] text-zinc-500">We verify manually within a few hours</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] text-zinc-400 uppercase mb-1 font-black tracking-wider">Full Name *</label>
                      <input
                        type="text" value={upiForm.full_name}
                        onChange={e => setUpiForm({ ...upiForm, full_name: e.target.value })}
                        placeholder="As per your UPI app"
                        className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/30 outline-none transition-all"
                        data-testid="upi-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-400 uppercase mb-1 font-black tracking-wider">Phone Number *</label>
                      <input
                        type="tel" value={upiForm.phone}
                        onChange={e => setUpiForm({ ...upiForm, phone: e.target.value })}
                        placeholder="+91 9999999999"
                        className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/30 outline-none transition-all"
                        data-testid="upi-input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-400 uppercase mb-1 font-black tracking-wider">UTR / Transaction ID *</label>
                    <input
                      type="text" value={upiForm.utr}
                      onChange={e => setUpiForm({ ...upiForm, utr: e.target.value })}
                      placeholder="12-digit reference from your UPI app"
                      className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-mono focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/30 outline-none transition-all"
                      data-testid="upi-input-utr"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-400 uppercase mb-1 font-black tracking-wider">Payment Screenshot * <span className="text-zinc-600 font-normal normal-case">(max 5MB)</span></label>
                    <input
                      ref={fileRef} type="file" accept="image/*"
                      onChange={e => setUpiScreenshot(e.target.files?.[0] || null)}
                      className="hidden" data-testid="upi-input-file"
                    />
                    <button
                      type="button" onClick={() => fileRef.current?.click()}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg transition-all ${upiScreenshot ? 'bg-brand-positive/10 border border-brand-positive/30 text-brand-positive' : 'bg-[#050505] border border-dashed border-white/20 text-zinc-300 hover:border-brand-primary hover:text-white'}`}
                      data-testid="upi-pick-file"
                    >
                      {upiScreenshot ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                      <span className="text-sm truncate max-w-[260px]">{upiScreenshot ? upiScreenshot.name : 'Click to upload screenshot'}</span>
                    </button>
                    {upiScreenshot && (
                      <div className="mt-1 text-[10px] text-zinc-500">{(upiScreenshot.size / 1024).toFixed(0)} KB · {upiScreenshot.type}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit CTA */}
              <button
                onClick={handleUpiSubmit} disabled={purchasing}
                className="relative w-full py-4 bg-gradient-to-br from-brand-primary to-[#00B8E0] text-black font-black rounded-xl hover:shadow-[0_0_45px_rgba(0,212,255,0.55)] hover:scale-[1.005] active:scale-[0.99] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 overflow-hidden group"
                data-testid="upi-submit-btn"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                {purchasing ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Shield className="w-4 h-4" /> Submit Payment Proof</>}
              </button>

              {/* Trust footer */}
              <div className="pt-2 border-t border-white/5">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { v: '2,400+', l: 'UPI Payments' },
                    { v: '< 2h', l: 'Avg Review' },
                    { v: '99.4%', l: 'Approval' },
                  ].map((s, i) => (
                    <div key={i} className="text-center p-2 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="text-brand-primary text-sm font-black font-mono leading-tight">{s.v}</div>
                      <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
                  AYRONN PAY uses manual UTR + screenshot verification for India-compliant compliance.<br className="hidden sm:block" />
                  Verified submissions trigger automatic account activation.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
